import React, {
  FormEvent,
  KeyboardEvent,
} from 'react';

import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import strings from '../../../localization';

import styles from './styles';
import VoiceWaveform from './VoiceWaveform';

interface IConversationComposer {
  getInputByteFrequencyData: () => Uint8Array;
  inputPlaceholder: string;
  isConnected: boolean;
  isTransitioning: boolean;
  isVoiceMode: boolean;
  onInputKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onSubmit: (event: FormEvent) => void;
  onTextInputChange: (value: string) => void;
  onTextModeButton: () => void;
  onVoiceButton: () => void;
  textInput: string;
  textInputRef: React.RefObject<HTMLInputElement>;
  voiceActionLabel: string;
  voiceStageRef: React.RefObject<HTMLDivElement>;
  voiceStatusLabel: string;
}

const ConversationComposer = ({
  getInputByteFrequencyData,
  inputPlaceholder,
  isConnected,
  isTransitioning,
  isVoiceMode,
  onInputKeyDown,
  onSubmit,
  onTextInputChange,
  onTextModeButton,
  onVoiceButton,
  textInput,
  textInputRef,
  voiceActionLabel,
  voiceStageRef,
  voiceStatusLabel,
}: IConversationComposer) =>
  (
    <Box sx={styles.composerContainer}>
      {isVoiceMode ? (
        <Box sx={styles.voiceComposer}>
          <Button
            type="button"
            variant="outlined"
            startIcon={<ChatBubbleOutlineRoundedIcon />}
            disabled={isTransitioning || !isConnected}
            onClick={onTextModeButton}
            sx={styles.textModeButton}
          >
            {strings.chatInTextMode}
          </Button>

          <Box
            ref={voiceStageRef}
            aria-live="polite"
            role="status"
            tabIndex={-1}
            sx={styles.voiceStage}
          >
            <Box sx={styles.voiceMicIndicator}>
              <MicRoundedIcon sx={styles.voiceMicIcon} />
            </Box>
            <Box sx={styles.voiceWaveContent}>
              <VoiceWaveform
                getInputByteFrequencyData={getInputByteFrequencyData}
                isActive={isConnected}
              />
              <Typography sx={styles.voiceStatusText}>
                {voiceStatusLabel}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box component="form" sx={styles.composer} onSubmit={onSubmit}>
          <TextField
            inputRef={textInputRef}
            value={textInput}
            onChange={(event) =>
              onTextInputChange(event.target.value)}
            disabled={isTransitioning || !isConnected}
            placeholder={inputPlaceholder}
            multiline
            maxRows={4}
            onKeyDown={onInputKeyDown}
            variant="standard"
            sx={styles.input}
            InputProps={{
              disableUnderline: true,
              inputProps: { 'aria-label': strings.typeYourMessage },
            }}
          />

          <Tooltip title={voiceActionLabel}>
            <span>
              <IconButton
                type="button"
                aria-label={voiceActionLabel}
                disabled={isTransitioning || !isConnected}
                onClick={onVoiceButton}
                sx={styles.voiceButton}
              >
                <GraphicEqRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={strings.send}>
            <span>
              <IconButton
                type="submit"
                aria-label={strings.send}
                disabled={
                  !textInput.trim()
                  || isTransitioning
                  || !isConnected
                }
                sx={styles.sendButton}
              >
                <SendRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      <Box sx={styles.composerFooter}>
        <Typography sx={styles.composerHint}>
          {isVoiceMode
            ? strings.voiceInputListeningHint
            : strings.pressEnterToSend}
        </Typography>
      </Box>
    </Box>
  );

export default ConversationComposer;
